import { Monitor, Moon, Sun, type LucideIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTheme } from "@/context/ThemeContext";
import { useToast } from "@/hooks/use-toast";
import { isAppearance, type Appearance } from "@/lib/theme";

const OPTIONS: { value: Appearance; label: string; description: string; icon: LucideIcon }[] = [
  { value: "light", label: "Light", description: "Always use the light theme.", icon: Sun },
  { value: "dark", label: "Dark", description: "Always use the dark theme.", icon: Moon },
  { value: "system", label: "System", description: "Match your device's setting and switch automatically.", icon: Monitor },
];

export function PreferencesSection() {
  const { appearance, setAppearance } = useTheme();
  const { toast } = useToast();

  const onChange = (value: string) => {
    if (!isAppearance(value)) return;
    setAppearance(value).catch(() =>
      toast({ variant: "destructive", title: "Couldn't save your appearance", description: "Please try again." }),
    );
  };

  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle id="appearance-heading" className="text-base">
          Appearance
        </CardTitle>
        <CardDescription>Choose how Waggle looks on this device and every device you sign in on.</CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup value={appearance} onValueChange={onChange} aria-labelledby="appearance-heading" className="grid-cols-1 gap-3 sm:grid-cols-3">
          {OPTIONS.map(({ value, label, description, icon: Icon }) => (
            <Label
              key={value}
              htmlFor={`appearance-${value}`}
              className="flex cursor-pointer items-start gap-3 rounded-xl border p-4 font-normal transition-colors hover:bg-muted has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-accent"
            >
              <RadioGroupItem value={value} id={`appearance-${value}`} className="mt-1" data-testid={`appearance-${value}`} />
              <span className="flex flex-col gap-1">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <Icon aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
                  {label}
                </span>
                <span className="text-sm text-muted-foreground">{description}</span>
              </span>
            </Label>
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  );
}
